<script setup lang="ts">
import { applyDivGridResizableColumns } from '@/helpers/global-items/applyDivGridResizableColumns'
import type { TableHeader } from '@/models/general/table'
import { useUtilityStore } from '@/store/utility'

export interface DivTableSortItem {
  key: string
  order: 'asc' | 'desc'
}

export interface DivTableOptions {
  page: number
  itemsPerPage: number
  sortBy: DivTableSortItem[]
}

export interface DivTableItemsPerPageOption {
  title: string
  value: number
}

interface Props {
  /**
   * v-model: array of selected item values (e.g. ids).
   * Matches VDataTableServer `v-model`.
   */
  modelValue?: any[]
  items?: any[]
  /**
   * Total number of items on the server (for pagination).
   * Matches VDataTableServer `itemsLength`.
   */
  itemsLength?: number
  /**
   * Column definitions. Columns with align === '' are visible.
   * Matches VDataTableServer `headers`.
   */
  headers?: TableHeader[]
  loading?: boolean | string
  page?: number
  itemsPerPage?: number
  itemsPerPageOptions?: (number | DivTableItemsPerPageOption)[]
  noDataText?: string
  /**
   * Property of each item used as a unique identifier.
   * Matches VDataTableServer `itemValue`.
   */
  itemValue?: string
  showSelect?: boolean
  itemSelectable?: boolean | string
  hover?: boolean
  sortBy?: DivTableSortItem[]
  expanded?: any[]
  /**
   * Unique DOM id for the grid root element, used by applyDivGridResizableColumns.
   */
  gridId: string
  /**
   * Storage key for persisting column widths (via utilityStore).
   */
  gridStorageId: string
  /**
   * Optional classes to add to the inner grid table element (div-grid-table).
   */
  tableClass?: string | string[] | Record<string, boolean>
  /**
   * Optional classes to add to the inner grid table element (div-table-wrapper).
   */
  wrapperClass?: string | string[] | Record<string, boolean>
  /**
     * Optional classes to add to the inner grid table element (div-table-bottom-scrollbar).
   */
  scrollbarClass?: string | string[] | Record<string, boolean>
  /**
   * Minimum width for auto-sized columns. Defaults to '120px'.
   */
  defaultColumnMinWidth?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  items: () => [],
  itemsLength: 0,
  headers: () => [],
  loading: false,
  page: 1,
  itemsPerPage: 20,
  itemsPerPageOptions: () => [15, 20, 50, 100],
  noDataText: 'No data available',
  itemValue: 'id',
  showSelect: false,
  itemSelectable: true,
  hover: false,
  sortBy: () => [],
  expanded: () => [],
  tableClass: () => (''),
  wrapperClass: () => (''),
  scrollbarClass: () => (''),
  defaultColumnMinWidth: '120px',
})

// Generate a deterministic fallback id based on visible header keys so
// saved column widths persist across reloads when caller doesn't provide ids.
const hashString = (s: string) => {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i)
  return Math.abs(h).toString(36).slice(0, 8)
}

const _generatedGridId = computed(() => {
  const keys = visibleHeaders.value.map(h => h.key).join('|') || 'default'
  return `div-table-${hashString(keys)}`
})

const effectiveGridId = computed(() => props.gridId || _generatedGridId.value)
const effectiveGridStorageId = computed(() => props.gridStorageId || `${effectiveGridId.value}_storage`)

interface Emits {
  (e: 'update:modelValue', value: any[]): void
  (e: 'update:expanded', value: any[]): void
  (e: 'update:page', value: number): void
  (e: 'update:itemsPerPage', value: number): void
  (e: 'update:sortBy', value: DivTableSortItem[]): void
  /**
   * Emitted whenever page, itemsPerPage, or sortBy changes.
   * Matches VDataTableServer `update:options`.
   */
  (e: 'update:options', value: DivTableOptions): void
}

const emit = defineEmits<Emits>()

const internalSortBy = ref<DivTableSortItem[]>([...(props.sortBy || [])])

watch(
  () => props.sortBy,
  value => {
    internalSortBy.value = [...(value || [])]
  },
  { deep: true },
)

// ─── Visible headers ──────────────────────────────────────────────────────────
// Convention in this project: headers with align === '' are visible columns.
const visibleHeaders = computed<TableHeader[]>(() =>
  (props.headers || []).filter(h => (h.align ?? '') === ''),
)

// ─── Pagination ───────────────────────────────────────────────────────────────
const visibleRangeText = computed(() => {
  const totalItems = Math.max(0, Number(props.itemsLength || 0))
  const perPage = Math.max(1, Number(props.itemsPerPage || 20))
  const currentPage = Math.max(1, Number(props.page || 1))

  const startIndex = Math.min((currentPage - 1) * perPage, totalItems)
  const start = totalItems === 0 ? 0 : startIndex + 1
  const end = Math.min(startIndex + perPage, totalItems)

  return `${start}-${end} of ${totalItems}`
})

const totalPages = computed(() =>
  Math.max(1, Math.ceil((props.itemsLength || 0) / (props.itemsPerPage || 20))),
)

const normalizedItemsPerPageOptions = computed<DivTableItemsPerPageOption[]>(() =>
  (props.itemsPerPageOptions || []).map(opt =>
    typeof opt === 'number' ? { title: String(opt), value: opt } : opt,
  ),
)

const utilityStore = useUtilityStore()

const onUpdateItemsPerPage = (value: number) => {
  utilityStore.setTableItemsPerPage(effectiveGridStorageId.value, value)
  emit('update:itemsPerPage', value)
  emit('update:options', { page: 1, itemsPerPage: value, sortBy: internalSortBy.value })
}

const onChangePage = (nextPage: number) => {
  if (nextPage < 1 || nextPage > totalPages.value) return
  emit('update:page', nextPage)
  emit('update:options', { page: nextPage, itemsPerPage: props.itemsPerPage, sortBy: internalSortBy.value })
}

// ─── Row selection ────────────────────────────────────────────────────────────
const selectedSet = computed(() => new Set(props.modelValue || []))

const isItemSelectable = (item: any) => {
  if (props.itemSelectable === true || props.itemSelectable === undefined) return true
  if (props.itemSelectable === false) return false

  const selectableField = props.itemSelectable as string
  return !!item?.[selectableField]
}

const selectableItemsOnPage = computed(() =>
  (props.items || []).filter(item => isItemSelectable(item)),
)

const isAllSelectedOnPage = computed(() => {
  if (!selectableItemsOnPage.value.length) return false
  return selectableItemsOnPage.value.every(item => selectedSet.value.has(item[props.itemValue]))
})

const isIndeterminate = computed(() => {
  if (!selectableItemsOnPage.value.length) return false
  const count = selectableItemsOnPage.value
    .filter(item => selectedSet.value.has(item[props.itemValue])).length
  return count > 0 && count < selectableItemsOnPage.value.length
})

const handleToggleSelectAll = () => {
  if (isAllSelectedOnPage.value) {
    const pageIds = new Set(selectableItemsOnPage.value.map(item => item[props.itemValue]))
    emit('update:modelValue', (props.modelValue || []).filter(v => !pageIds.has(v)))
  } else {
    const merged = new Set(props.modelValue || [])
    for (const item of selectableItemsOnPage.value) merged.add(item[props.itemValue])
    emit('update:modelValue', Array.from(merged))
  }
}

const handleToggleRow = (itemVal: any) => {
  console.log(itemVal)
  const next = new Set(props.modelValue || [])
  next.has(itemVal) ? next.delete(itemVal) : next.add(itemVal)
  emit('update:modelValue', Array.from(next))
}

const isExpanded = (item: any) => (props.expanded || []).includes(item?.[props.itemValue])

const toggleExpand = (item: any) => {
  const currentExpanded = new Set(props.expanded || [])
  const itemId = item?.[props.itemValue]

  if (currentExpanded.has(itemId)) {
    currentExpanded.delete(itemId)
  } else {
    currentExpanded.add(itemId)
  }

  emit('update:expanded', Array.from(currentExpanded))
}

// ─── Sorting ──────────────────────────────────────────────────────────────────
const getCurrentSort = (key: string): DivTableSortItem | null =>
  internalSortBy.value.find(s => s.key === key) ?? null

const getSortOrder = (key: string) => getCurrentSort(key)?.order ?? null

const getSortIcon = (key: string) => {
  const order = getSortOrder(key)

  if (order === 'desc') return 'mdi-keyboard-arrow-down'

  return 'mdi-keyboard-arrow-up'
}

const handleHeaderSort = (header: TableHeader) => {
  if (!header.sortable) return

  const current = getCurrentSort(header.key)
  let nextSortBy: DivTableSortItem[]

  if (!current) {
    nextSortBy = [{ key: header.key, order: 'asc' }]
  } else if (current.order === 'asc') {
    nextSortBy = [{ key: header.key, order: 'desc' }]
  } else {
    nextSortBy = []
  }

  internalSortBy.value = nextSortBy
  emit('update:sortBy', nextSortBy)
  emit('update:options', { page: 1, itemsPerPage: props.itemsPerPage, sortBy: nextSortBy })
}

// ─── Grid layout ──────────────────────────────────────────────────────────────
const gridTemplateColumnsManual = ref<string | null>(null)

const normalizeColumnWidth = (width?: number | string) => {
  if (width === undefined || width === null || width === '') return null
  return typeof width === 'number' ? `${width}px` : width
}

const gridTemplateColumns = computed(() => {
  if (gridTemplateColumnsManual.value) return gridTemplateColumnsManual.value

  const selectPrefix = props.showSelect ? '40px ' : ''
  const headerColumns = visibleHeaders.value
    .map(header => {
      const width = normalizeColumnWidth(header.width)
      return width ? `minmax(${width}, ${width})` : `minmax(${props.defaultColumnMinWidth}, 1fr)`
    })
    .join(' ')

  return `${selectPrefix}${headerColumns}`
})

const gridResizeConfiguration = computed(() => JSON.stringify({
  gridId: effectiveGridId.value,
  storageId: effectiveGridStorageId.value,
  showSelect: props.showSelect,
  headers: visibleHeaders.value.map(header => [header.key, normalizeColumnWidth(header.width)]),
}))

const cleanupGridResizableColumnsRef = ref<(() => void) | null>(null)
let gridResizeSyncGeneration = 0

const syncGridResizableColumns = () => {
  const generation = ++gridResizeSyncGeneration
  gridTemplateColumnsManual.value = null
  cleanupGridResizableColumnsRef.value?.()
  cleanupGridResizableColumnsRef.value = null

  nextTick(() => {
    if (generation !== gridResizeSyncGeneration) return
    const gridRoot = gridTableRef.value
    if (!gridRoot) return

    cleanupGridResizableColumnsRef.value = applyDivGridResizableColumns(
      gridRoot,
      effectiveGridStorageId.value,
      templateColumns => {
        gridTemplateColumnsManual.value = templateColumns
        updateBottomInnerWidth()
      },
    )
  })
}

// ─── Bottom scrollbar sync ────────────────────────────────────────────────────
const tableWrapperRef = ref<HTMLElement | null>(null)
const gridTableRef = ref<HTMLElement | null>(null)
const bottomScrollbarRef = ref<HTMLElement | null>(null)
const bottomInnerRef = ref<HTMLElement | null>(null)
const cleanupDragScrollRef = ref<(() => void) | null>(null)

const updateBottomInnerWidth = () => {
  nextTick(() => {
    // Use the direct ref to avoid ambiguous getElementById when the consumer
    // also puts an id attribute on the <DivTable> component (which falls through
    // to the root element, creating a duplicate id in the DOM).
    const tableEl = gridTableRef.value
    if (!tableEl || !bottomInnerRef.value) return
    bottomInnerRef.value.style.width = `${tableEl.scrollWidth}px`
  })
}

const initBottomScrollbar = () => {
  updateBottomInnerWidth()
  const wrapper = tableWrapperRef.value
  const bottom = bottomScrollbarRef.value
  if (!wrapper || !bottom) return

  const onWrapperScroll = () => { bottom.scrollLeft = wrapper.scrollLeft }
  const onBottomScroll = () => { wrapper.scrollLeft = bottom.scrollLeft }

  wrapper.addEventListener('scroll', onWrapperScroll)
  bottom.addEventListener('scroll', onBottomScroll)
  window.addEventListener('resize', updateBottomInnerWidth)

  onUnmounted(() => {
    wrapper.removeEventListener('scroll', onWrapperScroll)
    bottom.removeEventListener('scroll', onBottomScroll)
    window.removeEventListener('resize', updateBottomInnerWidth)
  })
}

const initDragScroll = () => {
  const wrapper = tableWrapperRef.value
  if (!wrapper) return

  cleanupDragScrollRef.value?.()

  let isDragging = false
  let hasMoved = false
  let startX = 0
  let startScrollLeft = 0

  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging) return

    const deltaX = e.clientX - startX
    if (Math.abs(deltaX) > 3) hasMoved = true

    wrapper.scrollLeft = startScrollLeft - deltaX * 1.5
    e.preventDefault()
  }

  const onMouseUp = () => {
    isDragging = false
    wrapper.style.cursor = 'grab'
    wrapper.style.userSelect = ''

    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('mouseup', onMouseUp)
  }

  const onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) return

    const target = e.target as HTMLElement

    // Keep header sorting/resizing and control clicks working as normal.
    if (target.closest('.div-grid-header')) return
    if (target.closest('a, button, input, textarea, select, label, [role="button"], .v-btn, .v-input, .v-field, .v-selection-control')) return

    isDragging = true
    hasMoved = false
    startX = e.clientX
    startScrollLeft = wrapper.scrollLeft
    wrapper.style.cursor = 'grabbing'
    wrapper.style.userSelect = 'none'

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
  }

  const onClickCapture = (e: MouseEvent) => {
    if (!hasMoved) return
    e.preventDefault()
    e.stopPropagation()
    hasMoved = false
  }

  wrapper.addEventListener('mousedown', onMouseDown)
  wrapper.addEventListener('mouseleave', onMouseUp)
  wrapper.addEventListener('click', onClickCapture, true)
  wrapper.style.cursor = 'grab'

  cleanupDragScrollRef.value = () => {
    onMouseUp()
    wrapper.removeEventListener('mousedown', onMouseDown)
    wrapper.removeEventListener('mouseleave', onMouseUp)
    wrapper.removeEventListener('click', onClickCapture, true)
    wrapper.style.cursor = ''
    wrapper.style.userSelect = ''
  }
}

onMounted(() => {
  // Restore persisted items-per-page if the user previously changed it.
  // Emitting before isMounted guards fire means parent state is updated before
  // the first API call, and URL-sync pages will still override via onMounted.
  const storedPerPage = utilityStore.getTableItemsPerPage(effectiveGridStorageId.value)
  if (storedPerPage !== null && storedPerPage !== props.itemsPerPage) {
    emit('update:itemsPerPage', storedPerPage)
    emit('update:options', { page: props.page, itemsPerPage: storedPerPage, sortBy: internalSortBy.value })
  }

  syncGridResizableColumns()
  initBottomScrollbar()
  initDragScroll()
})

onUnmounted(() => {
  gridResizeSyncGeneration += 1
  cleanupGridResizableColumnsRef.value?.()
  cleanupDragScrollRef.value?.()
})

watch(
  gridResizeConfiguration,
  () => {
    syncGridResizableColumns()
    updateBottomInnerWidth()
  },
  { flush: 'post' },
)

watch(
  () => [props.items?.length, props.page, props.itemsPerPage],
  () => {
    updateBottomInnerWidth()
  },
)
</script>

<template>
  <div class="lcx-div-table">
    <!-- Matches VDataTableServer #top slot -->
    <slot name="top" />

    <div ref="tableWrapperRef" class="div-table-wrapper" :class="props.wrapperClass">
      <div ref="gridTableRef" :id="effectiveGridId" class="div-grid-table" :class="props.tableClass">

        <!-- ── Header ── -->
        <div class="div-grid-header" :style="{ gridTemplateColumns }">
          <div v-if="showSelect" class="grid-cell select-col">
            <VCheckbox :model-value="isAllSelectedOnPage" :indeterminate="isIndeterminate" density="compact"
              hide-details @update:modelValue="handleToggleSelectAll" />
          </div>

          <div v-for="header in visibleHeaders" :key="header.key"
            :class="['grid-cell', header.sortable ? 'sortable' : '']" :data-col-width="header.width ?? ''"
            :data-col-key="header.key"
            @click="handleHeaderSort(header)">
            <!-- Matches VDataTableServer #header.{key} slot -->
            <slot :name="`header.${header.key}`" :column="header">
              <slot :name="`column.${header.key}`" :column="header">
                <div class="d-flex align-center" style="gap: 6px">
                  <span>{{ header.title }}</span>
                  <span v-if="header.sortable" class="sort-indicator">
                    <VIcon :icon="getSortIcon(header.key)" size="14" class="sort-icon"
                      :class="{ 'sort-icon--inactive': !getSortOrder(header.key) }" />
                  </span>
                </div>
              </slot>
            </slot>
          </div>
        </div>

        <!-- ── Body ── -->
        <div class="div-grid-body">
          <div v-if="loading" class="div-grid-feedback">
            <slot name="loading">
              <VProgressLinear indeterminate color="primary" />
            </slot>
          </div>

          <div v-else-if="!items?.length" class="div-grid-feedback text-center py-4">
            <slot name="no-data">{{ noDataText }}</slot>
          </div>

          <template v-else>
            <template v-for="item in items" :key="item[itemValue]">
              <div class="div-grid-row" :style="{ gridTemplateColumns }">
                <div v-if="showSelect" class="grid-cell select-col">
                  <VCheckbox :model-value="selectedSet.has(item[itemValue])" density="compact" hide-details
                    :disabled="!isItemSelectable(item)" @update:modelValue="() => handleToggleRow(item[itemValue])" />
                </div>

                <div v-for="header in visibleHeaders" :key="`${item[itemValue]}-${header.key}`" class="grid-cell">
                  <!--
                    Matches VDataTableServer #item.{key} slot.
                    Slot binding: { item: { raw: <row data> } }
                    to be compatible with existing VDataTableServer slot usages.
                  -->
                  <slot :name="`item.${header.key}`" :item="{ raw: item }" :toggleExpand="toggleExpand"
                    :isExpanded="isExpanded">
                    <span>{{ item[header.key] ?? '' }}</span>
                  </slot>
                </div>
              </div>

              <div v-if="isExpanded(item)" class="div-grid-expanded-row" :style="{ gridTemplateColumns }">
                <div class="grid-cell expanded-cell"
                  :style="{ gridColumn: `1 / span ${visibleHeaders.length + (showSelect ? 1 : 0)}` }">
                  <slot name="expanded-row" :item="{ raw: item }" :columns="visibleHeaders" />
                </div>
              </div>
            </template>
          </template>
        </div>

      </div>
    </div>

    <!-- Sticky per-table horizontal scrollbar (synced with table horizontal scroll) -->
    <div ref="bottomScrollbarRef" class="div-table-bottom-scrollbar" :class="props.scrollbarClass" aria-hidden="true">
      <div ref="bottomInnerRef" class="div-table-bottom-scrollbar-inner" />
    </div>

    <!-- Matches VDataTableServer #bottom slot – falls back to built-in pagination -->
    <slot name="bottom">
      <div class="div-table-footer mt-3">
        <span>Rows per page</span>
        <VSelect :model-value="itemsPerPage" :items="normalizedItemsPerPageOptions" item-title="title"
          item-value="value" density="compact" hide-details style="max-inline-size: 100px"
          @update:modelValue="onUpdateItemsPerPage" />
        <span>{{ visibleRangeText }}</span>
        <div style="display: contents;">
          <VBtn class="page-button" icon="material-symbols:first-page" variant="text" :disabled="page <= 1"
            @click="onChangePage(1)" />
          <VBtn class="page-button" icon="tabler-chevron-left" variant="text" :disabled="page <= 1"
            @click="onChangePage(page - 1)" />
        </div>
        <span>{{ page }}</span>
        <div style="display: contents;">
          <VBtn class="page-button" icon="tabler-chevron-right" variant="text" :disabled="page >= totalPages"
            @click="onChangePage(page + 1)" />
          <VBtn class="page-button" icon="material-symbols:last-page" variant="text" :disabled="page >= totalPages"
            @click="onChangePage(totalPages)" />
        </div>
      </div>
    </slot>
  </div>
</template>

<style lang="css">
.main {
  overflow: scroll;
  /* overflow-x: hidden; */
}

.lcx-div-table {
  container: div-table / inline-size;
  padding: 22px 0 !important;
  display: flex;
  flex-direction: column;

  .div-table-wrapper {
    overflow-x: scroll;
    overflow-y: auto;
    max-height: calc(80vh - 160px);
  }

  /* Hide the native horizontal scrollbar — the custom sticky bar replaces it.
     ::-webkit-scrollbar height:0 hides horizontal only in Chrome/Edge/Safari.
     Firefox: no CSS-only way to hide just horizontal while keeping vertical;
     native horizontal scrollbar may still show there (acceptable fallback). */
  .div-table-wrapper::-webkit-scrollbar {
    height: 0;
    width: 8px;
  }

  .div-table-wrapper::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.04);
  }

  .div-table-wrapper::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.25);
    border-radius: 6px;
  }

  .div-grid-table {
    min-width: max-content;
    overflow: visible;
  }

  .div-grid-header,
  .div-grid-row,
  .div-grid-expanded-row {
    display: grid;
    align-items: stretch;
  }

  .div-grid-header {
    position: sticky;
    top: 0;
    z-index: 3;
    min-width: 100%;
    width: max-content;
    background: rgba(var(--v-theme-surface), 0.8);
    backdrop-filter: blur(4px);
    padding-right: 4px;

    .grid-cell {
      border-top: none;
      text-transform: uppercase;
      padding: 12px 10px;
      align-items: center;
      display: flex;
      cursor: default;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      border-right: 1px solid rgba(0, 0, 0, 0.1);
    }
  }

  .div-grid-row:hover {
    background: rgba(var(--v-theme-on-surface), 0.04);
  }

  .div-grid-expanded-row {
    background: rgba(var(--v-theme-on-surface), 0.03);
  }

  .grid-cell {
    height: 100%;
    padding: 0 10px;
    min-height: 38px;
    display: flex;
    align-items: center;
    font-size: 15px;
    border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    overflow: hidden;
    overflow-wrap: break-word;
    white-space: normal;
    border-right: none;
  }

  .grid-cell.sortable {
    cursor: pointer;
    user-select: none;
  }

  .grid-cell.select-col {
    justify-content: center;
  }

  .grid-cell.expanded-cell {
    padding: 12px 10px;
    align-items: stretch;
  }

  /* Resize handle inserted by applyDivGridResizableColumns */
  .div-grid-resize-handle {
    background: transparent;
    width: 6px;
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    cursor: col-resize;
    z-index: 5;
  }

  .div-grid-resize-handle:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  .sort-indicator {
    font-size: 0.8rem;
    opacity: 0.9;
    display: inline-flex;
    align-items: center;
  }

  .sort-indicator .sort-icon {
    transition: opacity 0.15s ease, color 0.15s ease;
  }

  .sort-indicator .sort-icon--inactive {
    opacity: 0;
  }

  .div-grid-header .grid-cell.sortable:hover .sort-indicator .sort-icon {
    color: rgb(var(--v-theme-primary));
  }

  .div-grid-header .grid-cell.sortable:hover .sort-indicator .sort-icon--inactive {
    opacity: 0.7;
  }

  .div-grid-feedback {
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .div-table-bottom-scrollbar {
    position: sticky;
    bottom: 0;
    height: 14px;
    overflow-x: scroll;
    overflow-y: hidden;
    z-index: 3;
    background: rgb(var(--v-theme-surface));
  }

  .div-table-bottom-scrollbar-inner {
    height: 1px;
  }

  .div-table-bottom-scrollbar::-webkit-scrollbar {
    height: 10px;
  }

  .div-table-bottom-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.26);
  }

  .div-table-bottom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.52);
    border-radius: 7px;
  }

  .div-table-footer {
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;

    .page-button {
      min-width: 32px !important;
      width: 32px !important;
      height: 32px !important;
      padding: 0;
    }

    .page-button.v-btn--disabled {
      opacity: 0.3 !important;
    }
  }


  @container (inline-size < 500px) {
    .div-table-footer {
      gap: 4px;
      font-size: 12px;

      .page-button {
        min-width: 16px !important;
        inline-size: 16px !important;
        padding: 0;
      }

      .v-field {
        font-size: 12px;
        padding-right: 8px;
        max-width: 76px;

        .v-field__input {
          padding-left: 8px;
        }
      }
    }
  }

  .div-grid-table.small-font {
    .grid-cell {
      font-size: 10px;
    }
  }

}
</style>
